import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CalendarClock,
  Eye,
  Image as ImageIcon,
  Megaphone,
  Package,
  Pencil,
  Plus,
  Rocket,
  Trash2,
} from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Button from "../../../components/Button/Button";
import Input from "../../../components/Input/Input";
import Notification from "../../../components/Notification/Notification";
import Pagination from "../../../components/Pagination/Pagination";
import APITable from "../../../components/Tables/APITable";
import Modal from "../../../components/Modal/Modal";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import { TableSkeleton } from "../../../components/Skeleton/StatsSkeleton.jsx";

/* ===================== SERVICES / UTILS ===================== */
import { apiGet, apiPost, apiDelete } from "../../../apiService";
import { can } from "../../../utils/permissions";
import { getErrorMessage } from "../../../utils/errorHelper";
import { getRole } from "../../../utils/auth";
import { BASE_PATH, formatDate } from "./featureUtils";

/* ===================== STYLES ===================== */
import styles from "./FeatureUpdates.module.scss";
/* Shared dashboard view-modal styles (Help Desk / Storage / Contact Requests) */
import hdStyles from "../../../components/HelpDesk/HelpDeskShared.module.scss";

const FeatureUpdates = () => {
  const navigate = useNavigate();
  const isSuperAdmin = getRole() === "Internal";

  const [featureUpdates, setFeatureUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState(""); // "" | Draft | Published
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, run }
  const [viewItem, setViewItem] = useState(null);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const notify = useCallback((message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3500);
  }, []);

  /* -------- super-admin (Internal) guard -------- */
  useEffect(() => {
    if (!isSuperAdmin) navigate("/admin/dashboard", { replace: true });
  }, [isSuperAdmin, navigate]);

  /* -------- list -------- */
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (statusFilter) qs.set("status", statusFilter);
      if (search) qs.set("search", search);
      const res = await apiGet("catalog", `/feature-updates?${qs.toString()}`);
      if (res?.status === "success") {
        setFeatureUpdates(res.data?.featureUpdates || []);
        const pg = res.data?.pagination || {};
        setTotalPages(pg.totalPages || 1);
        setTotal(pg.total || 0);
      } else {
        notify(res?.message || "Failed to fetch feature updates", "error");
      }
    } catch (error) {
      notify(
        getErrorMessage(error, "Failed to fetch feature updates"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, search, notify]);

  useEffect(() => {
    if (isSuperAdmin) fetchList();
  }, [isSuperAdmin, fetchList]);

  /* debounce search */
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* -------- row actions -------- */
  const doPublish = async (row) => {
    setBusyId(row.id);
    try {
      const res = await apiPost(
        "catalog",
        `/feature-updates/${row.id}/publish`,
      );
      notify(res?.message || "Feature update published", "success");
      fetchList();
    } catch (error) {
      notify(getErrorMessage(error, "Could not publish"), "error");
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (row) => {
    setBusyId(row.id);
    try {
      const res = await apiDelete("catalog", `/feature-updates/${row.id}`);
      notify(res?.message || "Feature update deleted", "success");
      if (featureUpdates.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchList();
    } catch (error) {
      notify(getErrorMessage(error, "Could not delete"), "error");
    } finally {
      setBusyId(null);
    }
  };

  const runConfirm = () => {
    const c = confirmDialog;
    setConfirmDialog(null);
    c?.run?.();
  };

  /* -------- table -------- */
  const columns = useMemo(
    () => [
      { label: "Title", key: "title" },
      { label: "Products", key: "products" },
      { label: "Media", key: "media" },
      { label: "Status", key: "status" },
      { label: "Published", key: "publishedAt" },
      { label: "Actions", key: "action" },
    ],
    [],
  );

  const renderCell = (row, key) => {
    if (key === "title") {
      return (
        <div className={styles["fu-cell-title"]}>
          <span className={styles["fu-cell-title__main"]}>{row.title}</span>
          <span className={styles["fu-cell-title__desc"]}>
            {row.description}
          </span>
        </div>
      );
    }
    if (key === "products") {
      const names = (row.products || []).map((p) => p.name).filter(Boolean);
      if (!names.length) return "—";
      return (
        <div className={styles["fu-pills"]}>
          {names.map((n, i) => (
            <span key={i} className={styles["fu-pill"]}>
              {n}
            </span>
          ))}
        </div>
      );
    }
    if (key === "media") return (row.media || []).length;
    if (key === "status") {
      const published = row.status === "Published";
      return (
        <span
          className={`${styles["fu-badge"]} ${
            published
              ? styles["fu-badge--published"]
              : styles["fu-badge--draft"]
          }`}
        >
          {row.status}
        </span>
      );
    }
    if (key === "publishedAt") return formatDate(row.publishedAt);
    if (key === "action") {
      return (
        <div className={styles["fu-actions"]}>
          <button
            type="button"
            className={styles["fu-icon-btn"]}
            title="View"
            onClick={() => setViewItem(row)}
          >
            <Eye size={18} />
          </button>
          {can("product-access", "feature-update-update") && (
            <button
              type="button"
              className={styles["fu-icon-btn"]}
              title="Edit"
              onClick={() => navigate(`${BASE_PATH}/${row.id}/edit`)}
            >
              <Pencil size={18} />
            </button>
          )}
          {row.status !== "Published" &&
            can("product-access", "feature-update-publish") && (
              <button
                type="button"
                className={`${styles["fu-icon-btn"]} ${styles["fu-icon-btn--publish"]}`}
                title="Publish"
                disabled={busyId === row.id}
                onClick={() =>
                  setConfirmDialog({
                    title: `Publish "${row.title}"? This will broadcast it to organization admins.`,
                    run: () => doPublish(row),
                  })
                }
              >
                <Rocket size={18} />
              </button>
            )}
          {can("product-access", "feature-update-delete") && (
            <button
              type="button"
              className={`${styles["fu-icon-btn"]} ${styles["fu-icon-btn--danger"]}`}
              title="Delete"
              disabled={busyId === row.id}
              onClick={() =>
                setConfirmDialog({
                  title: `Delete "${row.title}"? This action cannot be undone.`,
                  run: () => doDelete(row),
                })
              }
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      );
    }
    return row[key];
  };

  if (!isSuperAdmin) return null;

  return (
    <div className={styles["fu-page"]}>
      {notification && (
        <div className={styles["fu-toast"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["fu-header"]}>
        <div className={styles["fu-header__left"]}>
          <button
            type="button"
            className={styles["fu-back"]}
            onClick={() => navigate("/admin/product-access")}
            title="Back to Product Access"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>
          <div className={styles["fu-icon-wrapper"]}>
            <Megaphone size={24} strokeWidth={2.5} />
          </div>
          <div className={styles["fu-title-group"]}>
            <h2 className={styles["fu-title"]}>Feature Updates</h2>
            <p className={styles["fu-subtitle"]}>
              Announce product changes and broadcast them to organization
              admins.
            </p>
          </div>
        </div>
        {can("product-access", "feature-update-create") && (
          <Button
            text="New Update"
            icon={Plus}
            onClick={() => navigate(`${BASE_PATH}/new`)}
          />
        )}
      </div>

      {/* MAIN CARD */}
      <div className={styles["fu-card"]}>
        <div className={styles["fu-controls"]}>
          <div className={styles["fu-showing-text"]}>
            Showing {featureUpdates.length} of {total} feature update
            {total === 1 ? "" : "s"}
          </div>

          <div className={styles["fu-filters"]}>
            <div className={styles["fu-search-group"]}>
              <Input
                type="search"
                placeholder="Search by title..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <Input
              type="select"
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value);
              }}
              options={[
                { label: "All statuses", value: "" },
                { label: "Draft", value: "Draft" },
                { label: "Published", value: "Published" },
              ]}
            />
          </div>
        </div>

        <div className={styles["fu-table-wrapper"]}>
          {loading ? (
            <TableSkeleton columns={6} rows={5} />
          ) : featureUpdates.length === 0 ? (
            <div className={styles["fu-empty"]}>
              No feature updates yet. Click <strong>New Update</strong> to
              create one.
            </div>
          ) : (
            <APITable
              columns={columns}
              data={featureUpdates}
              renderCell={renderCell}
            />
          )}
        </div>

        {!loading && featureUpdates.length > 0 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            rowsPerPage={limit}
            onPageChange={setPage}
            onRowsPerPageChange={(n) => {
              setPage(1);
              setLimit(n);
            }}
          />
        )}
      </div>

      {/* VIEW MODAL */}
      {viewItem && (
        <Modal title="Feature Update Details" onClose={() => setViewItem(null)}>
          <div className={hdStyles["helpdesk-modal__container"]}>
            {/* SUMMARY */}
            <div className={hdStyles["helpdesk-modal__section"]}>
              <div className={hdStyles["helpdesk-modal__section-header"]}>
                <div className={hdStyles["helpdesk-modal__icon-wrapper"]}>
                  <Megaphone size={18} strokeWidth={2.5} />
                </div>
                <h3 className={hdStyles["helpdesk-modal__section-title"]}>
                  Update #{viewItem.id}
                </h3>
                <span
                  className={`${hdStyles["helpdesk-modal__status-badge"]} ${
                    hdStyles[
                      `helpdesk-modal__status-badge--${
                        viewItem.status === "Published" ? "resolved" : "closed"
                      }`
                    ]
                  }`}
                >
                  {viewItem.status}
                </span>
              </div>

              <h4 className={hdStyles["helpdesk-modal__subject"]}>
                {viewItem.title}
              </h4>
              <p className={hdStyles["helpdesk-modal__description"]}>
                {viewItem.description || "No description provided."}
              </p>
            </div>

            {/* PRODUCTS */}
            <div className={hdStyles["helpdesk-modal__section"]}>
              <div className={hdStyles["helpdesk-modal__section-header"]}>
                <div className={hdStyles["helpdesk-modal__icon-wrapper"]}>
                  <Package size={18} strokeWidth={2.5} />
                </div>
                <h3 className={hdStyles["helpdesk-modal__section-title"]}>
                  Products
                </h3>
              </div>
              {(viewItem.products || []).length > 0 ? (
                <div className={styles["fu-pills"]}>
                  {viewItem.products.map((p, i) => (
                    <span key={p.id ?? i} className={styles["fu-pill"]}>
                      {p.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className={styles["fu-hint"]}>No products linked.</p>
              )}
            </div>

            {/* TIMELINE */}
            <div className={hdStyles["helpdesk-modal__section"]}>
              <div className={hdStyles["helpdesk-modal__section-header"]}>
                <div className={hdStyles["helpdesk-modal__icon-wrapper"]}>
                  <CalendarClock size={18} strokeWidth={2.5} />
                </div>
                <h3 className={hdStyles["helpdesk-modal__section-title"]}>
                  Timeline
                </h3>
              </div>
              <div className={hdStyles["helpdesk-modal__grid"]}>
                <div className={hdStyles["helpdesk-modal__field"]}>
                  <span className={hdStyles["helpdesk-modal__field-label"]}>
                    Created
                  </span>
                  <span className={hdStyles["helpdesk-modal__field-value"]}>
                    {formatDate(viewItem.createdAt)}
                  </span>
                </div>
                <div className={hdStyles["helpdesk-modal__field"]}>
                  <span className={hdStyles["helpdesk-modal__field-label"]}>
                    Published
                  </span>
                  <span className={hdStyles["helpdesk-modal__field-value"]}>
                    {viewItem.publishedAt
                      ? `${formatDate(viewItem.publishedAt)}`
                      : "Not published yet"}
                  </span>
                </div>
                <div className={hdStyles["helpdesk-modal__field"]}>
                  <span className={hdStyles["helpdesk-modal__field-label"]}>
                    Last Updated
                  </span>
                  <span className={hdStyles["helpdesk-modal__field-value"]}>
                    {formatDate(viewItem.updatedAt)}
                  </span>
                </div>
                {viewItem.isDeleted && (
                  <div className={hdStyles["helpdesk-modal__field"]}>
                    <span className={hdStyles["helpdesk-modal__field-label"]}>
                      Deleted
                    </span>
                    <span className={hdStyles["helpdesk-modal__field-value"]}>
                      {formatDate(viewItem.deletedAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* MEDIA */}
            <div className={hdStyles["helpdesk-modal__section"]}>
              <div className={hdStyles["helpdesk-modal__section-header"]}>
                <div className={hdStyles["helpdesk-modal__icon-wrapper"]}>
                  <ImageIcon size={18} strokeWidth={2.5} />
                </div>
                <h3 className={hdStyles["helpdesk-modal__section-title"]}>
                  Media ({(viewItem.media || []).length})
                </h3>
              </div>
              {(viewItem.media || []).length > 0 ? (
                <div className={styles["fu-media-grid"]}>
                  {viewItem.media.map((m) =>
                    m.type === "video" ? (
                      <video
                        key={m.id}
                        src={m.url}
                        controls
                        className={styles["fu-media__thumb"]}
                      />
                    ) : (
                      <a
                        key={m.id}
                        href={m.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          src={m.url}
                          alt=""
                          className={styles["fu-media__thumb"]}
                        />
                      </a>
                    ),
                  )}
                </div>
              ) : (
                <p className={styles["fu-hint"]}>No media attached.</p>
              )}
            </div>

            {/* ACTIONS */}
            {can("product-access", "feature-update-update") && (
              <div className={styles["fu-view-actions"]}>
                <button
                  type="button"
                  className={`${hdStyles["helpdesk-modal__btn"]} ${hdStyles["helpdesk-modal__btn--primary"]}`}
                  onClick={() => navigate(`${BASE_PATH}/${viewItem.id}/edit`)}
                >
                  <Pencil size={16} strokeWidth={2.5} />
                  Edit Update
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      <ConfirmModal
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        onClose={() => setConfirmDialog(null)}
        onConfirm={runConfirm}
      />
    </div>
  );
};

export default FeatureUpdates;
